import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import wordmark114 from "../../../assets/brand/onboarding/wordmark-114.svg";
import instagramGlyph from "../../../assets/brand/onboarding/instagram-glyph.svg";
import tiktokGlyph from "../../../assets/brand/onboarding/tiktok-glyph.svg";
import dotGreen6 from "../../../assets/brand/onboarding/dot-green-6.svg";
import chevronDown from "../../../assets/brand/onboarding/chevron-down.svg";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// V2 onboarding pieces, sized from the Figma "Athlete Onboarding" frames.
// Every screen is a 28px-gap column of these blocks.

// Below 640px the sub line may break inside a long word (an email in "Sent
// to ..."), so it wraps in the 272px column of a 320px screen instead of
// pushing the page sideways.
export const TitleBlock = ({ eyebrow, title, sub, tone = "brand" }) => (
  <div className="flex flex-col gap-2">
    {eyebrow && <p className={`bm-overline ${tone === "critical" ? "!text-bm-critical" : "!text-bm-orange"}`}>{eyebrow}</p>}
    <h1 className="bm-h-l text-bm-ink">{title}</h1>
    {sub && <p className="bm-body-l text-bm-muted max-sm:break-words">{sub}</p>}
  </div>
);
// Kept for callers that compose the title block themselves.
export const Eyebrow = ({ children, tone = "brand" }) => <p className={`bm-overline ${tone === "critical" ? "!text-bm-critical" : "!text-bm-orange"}`}>{children}</p>;
export const Title = ({ children }) => <h1 className="bm-h-l text-bm-ink">{children}</h1>;
export const Sub = ({ children }) => <p className="bm-body-l text-bm-muted">{children}</p>;

export const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-[7px]">
    {label && <span className="bm-overline">{label}</span>}
    {children}
    {hint && <p className="bm-body-s text-bm-tertiary">{hint}</p>}
  </div>
);
export const FieldLabel = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="bm-overline block">{children}</label>;

// React 19 hands `ref` to the input with the other props.
export const Input = ({ error = false, className = "", ...props }) => (
  <input className={`ob-input ${error ? "ob-input--error" : ""} ${className}`} {...props} />
);

// The eye glyph of a password field's Show / Hide button (ChangePassword
// draws the same one).
export const EyeIcon = ({ off }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M3 3l18 18" />}
  </svg>
);

// A 56px V2 password field with a Show / Hide button on its right (owner
// review, 9/29: every password field gets one). The button is named "Show
// password" or "Hide password" for what a press does, and a 44px target on
// phones. Everything else (value, onChange, autoComplete, placeholder,
// aria-label, id) goes to the input as before. A shown password is hidden
// again as its form submits, so password managers read a password field
// when they offer to save it.
export const PasswordInput = ({ error = false, className = "", ...props }) => {
  const [show, setShow] = useState(false);
  const field = useRef(null);
  useEffect(() => {
    const form = field.current?.form;
    if (!form) return undefined;
    const hide = () => {
      if (field.current) field.current.type = "password";
      setShow(false);
    };
    form.addEventListener("submit", hide, true);
    return () => form.removeEventListener("submit", hide, true);
  }, []);
  return (
    <span className="relative block">
      <Input {...props} ref={field} type={show ? "text" : "password"} error={error} className={`!pr-[52px] ${className}`} />
      <button
        type="button"
        onClick={() => setShow((on) => !on)}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-[6px] top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-bm-tertiary hover:text-bm-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-bm-ink"
      >
        <EyeIcon off={show} />
      </button>
    </span>
  );
};

export const SelectInput = ({ className = "", children, ...props }) => (
  <span className={`relative block ${className}`}>
    <select className="ob-input" {...props}>{children}</select>
    <img src={chevronDown} alt="" className="pointer-events-none absolute right-[18px] top-1/2 h-2 w-[14px] -translate-y-1/2" />
  </span>
);

// Tinted notes take their fill from the bm status tokens. The success and
// warning text stay a darker shade of bm-positive / bm-attention so body
// copy keeps AA contrast on the tint.
export const Note = ({ children, tone = "neutral", wide = false, bordered = false }) => {
  if (tone === "critical") return <div className="rounded-[8px] bg-bm-critical/10 px-4 py-[14px] bm-body-m text-bm-critical">{children}</div>;
  if (tone === "success") return <div className="rounded-[8px] bg-bm-positive/10 px-4 py-[14px] bm-body-m text-[#1f6b3a]">{children}</div>;
  return <div className={`bg-bm-subtle bm-body-m text-bm-muted ${bordered ? "border border-bm-border" : ""} ${wide ? "rounded-[16px] px-[18px] py-4" : "rounded-[8px] px-4 py-[14px]"}`}>{children}</div>;
};

// A red note for a failed step. When the server says the email already has
// an account, it offers the way to log in.
const EMAIL_TAKEN = /already registered|already uses this email/i;
export const ErrorNote = ({ error, onLogin }) => (
  <Note tone="critical">
    {error}{EMAIL_TAKEN.test(error) && (<> <button type="button" className="underline" onClick={onLogin}>Log in instead</button></>)}
  </Note>
);

// Below 640px the badge sits 8px above the text (Figma 1759:1520).
export const WarnNote = ({ badge, children }) => (
  <div className="flex flex-col gap-3 rounded-[8px] bg-bm-attention/15 p-4 max-sm:gap-2">
    {badge && <span className="inline-flex h-[22px] w-fit items-center rounded-full bg-bm-attention px-[9px] bm-label-xs text-bm-ink">{badge}</span>}
    <p className="bm-body-m text-[#8a6d00]">{children}</p>
  </div>
);

export const DotNote = ({ children }) => (
  <div className="flex items-center gap-[10px] rounded-[8px] bg-bm-subtle px-[14px] py-3">
    <img src={dotGreen6} alt="" className="h-[6px] w-[6px]" />
    <span className="bm-body-s text-bm-muted">{children}</span>
  </div>
);

// The step's buttons. On phones, inside the AuthFrame column (`.ob-frame`),
// `ob-actions` makes it the white footer that sits at the bottom of a short
// screen and sticks to the bottom of a long one (onboarding.css). `rule`
// gives that footer its 1px top rule: of the onboarding steps Figma draws
// it on 13 Profile live only (1690:1272), which passes it on phones.
export const Actions = ({ rule = false, children }) => <div className={`ob-actions${rule ? " ob-actions--rule" : ""} flex flex-col gap-3 pt-2`}>{children}</div>;

const btnBase = "flex h-[52px] w-full items-center justify-center rounded-full px-6 bm-label-l transition-colors";
export const PrimaryBtn = ({ children, disabled, loading, className = "", ...props }) => (
  <button type="button" disabled={disabled || loading} className={`${btnBase} ${disabled || loading ? "cursor-not-allowed bg-bm-mutedbg text-bm-placeholder" : "bg-bm-orange text-white hover:bg-[#e63d00]"} ${className}`} {...props}>
    {loading ? "One moment..." : children}
  </button>
);
// A PrimaryBtn that goes to another route (and can carry route state).
export const PrimaryLink = ({ to, state, children }) => (
  <Link to={to} state={state} className={`${btnBase} bg-bm-orange text-white hover:bg-[#e63d00]`}>{children}</Link>
);
export const SecondaryBtn = ({ children, className = "", ...props }) => (
  <button type="button" className={`${btnBase} border border-bm-border bg-white text-bm-ink hover:bg-bm-subtle disabled:cursor-not-allowed disabled:text-bm-placeholder ${className}`} {...props}>{children}</button>
);
export const TextBtn = ({ children, brand = false, className = "", ...props }) => (
  <button type="button" className={`${btnBase} ${brand ? "text-bm-orange" : "text-bm-ink"} hover:underline ${className}`} {...props}>{children}</button>
);

// Figma "SegmentedControl · Mobile": a full-width 52px pill track with 44px
// segments. They share the track's width unless `segmentClassName` sets one
// (the athlete onboarding steps use fixed 167px segments).
//
// It is a radio group on every width, with roving focus: one tab stop (the
// picked segment, else the first), Left / Up and Right / Down move to the
// previous and next segment and pick it (wrapping at the ends), and Home /
// End pick the first and last.
const SEGMENT_STEP = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
export const SegmentedControl = ({ value, onChange, options, label, segmentClassName = "flex-1" }) => {
  const refs = useRef([]);
  const picked = options.findIndex(([v]) => v === value);
  const tabStop = picked >= 0 ? picked : 0;
  const onKeyDown = (i) => (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const count = options.length;
    let next;
    if (SEGMENT_STEP[e.key]) next = (i + SEGMENT_STEP[e.key] + count) % count;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = count - 1;
    else return;
    e.preventDefault();
    refs.current[next]?.focus();
    if (options[next][0] !== value) onChange(options[next][0]);
  };
  return (
    <div role="radiogroup" aria-label={label} className="flex h-[52px] w-full items-center rounded-full bg-bm-mutedbg p-1">
      {options.map(([v, text], i) => (
        <button
          key={v}
          ref={(el) => { refs.current[i] = el; }}
          type="button"
          role="radio"
          aria-checked={value === v}
          tabIndex={i === tabStop ? 0 : -1}
          onClick={() => onChange(v)}
          onKeyDown={onKeyDown(i)}
          className={`flex h-11 items-center justify-center rounded-full bm-label-l ${segmentClassName} ${value === v ? "bg-white text-bm-ink shadow-[0px_2px_6px_0px_rgba(0,0,0,0.08)]" : "text-bm-muted hover:text-bm-ink"}`}
        >
          {text}
        </button>
      ))}
    </div>
  );
};

export const Divider = () => (
  <div className="flex items-center gap-3 py-1">
    <span className="h-px w-[130px] bg-bm-border" />
    <span className="bm-body-s text-bm-tertiary">or</span>
    <span className="h-px w-[130px] bg-bm-border" />
  </div>
);

export const Lockup = ({ tagline }) => (
  <div className="flex flex-col items-center gap-6 pb-2 pt-6">
    <img src={wordmark114} alt="CACHE" className="h-[23.34px] w-[114px]" />
    <p className="text-center bm-body-m text-bm-muted">{tagline}</p>
  </div>
);

export const InlineLink = ({ text, label, onClick }) => (
  <div className="flex items-center justify-center gap-[6px]">
    <span className="bm-body-m text-bm-muted">{text}</span>
    <button type="button" onClick={onClick} className="bm-label-l text-bm-orange hover:underline">{label}</button>
  </div>
);

export const Fine = ({ children }) => <p className="text-center bm-body-s text-bm-tertiary">{children}</p>;

// Social sign-in buttons (Figma "social-btn": 56px, 1.5px #d9d9d9 border,
// 26px logo tile). Instagram and TikTok stay visible and disabled from 640px
// up until the TikTok Login Kit and Meta apps are approved.
export const SocialButton = ({ tile, glyph, label, enabled = true, onClick, busy = false, soon = false }) => (
  <button
    type="button"
    disabled={!enabled || busy}
    onClick={onClick}
    title={enabled ? "" : "Coming soon"}
    className={`flex h-14 w-full items-center justify-center gap-3 rounded-full border-[1.5px] border-bm-strong bg-white px-4 bm-label-l text-bm-ink ${enabled ? "hover:bg-bm-subtle" : "cursor-not-allowed opacity-60"}`}
  >
    <span className={`flex h-[26px] w-[26px] items-center justify-center rounded-[8px] ${tile}`}>{glyph}</span>
    {busy ? "One moment..." : label}
    {soon && !enabled && <span className="ml-1 rounded-full bg-bm-subtle px-2 py-[2px] bm-label-xs uppercase text-bm-tertiary">Soon</span>}
  </button>
);

// On phones (the AuthFrame phone theme) the group only offers what works:
// while Instagram and TikTok are switched off, their "Soon" buttons are left
// out and the `leading` Google button stands alone (Figma 1745:1311 and
// 2312:4215, decision D-o3). Once they are on, phones show them too.
export const SocialButtons = ({ label = "Continue with", enabled = false, onPick, leading = null }) => {
  const phone = usePhoneTheme();
  const showSocial = enabled || !phone;
  return (
    <div className="flex flex-col gap-[10px]">
      <p className="bm-overline">{label}</p>
      {leading}
      {showSocial && (
        <>
          <SocialButton tile="bg-[#e1306c]" glyph={<img src={instagramGlyph} alt="" className="h-4 w-4" />} label="Instagram" enabled={enabled} soon onClick={() => onPick && onPick("instagram")} />
          <SocialButton tile="bg-bm-ink" glyph={<img src={tiktokGlyph} alt="" className="h-4 w-4" />} label="TikTok" enabled={enabled} soon onClick={() => onPick && onPick("tiktok")} />
        </>
      )}
    </div>
  );
};

// Six digit boxes, 50x62, 12px radius, 10px apart (Figma "code-boxes").
// Below 640px the row fills the column and the boxes share it, 8px apart
// and at most 50px wide (Figma 1579:830), so all six fit from a 320px
// screen up. There the boxes also drop the forms plugin's 12px side
// padding: at 320 a box is about 39px wide, which would leave 13px for a
// 26px digit and clip it. The digit is centred, so nothing else moves.
export const CodeInput = ({ value, onChange, error = false, disabled = false }) => {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");
  useEffect(() => {
    refs.current[Math.min(value.length, 5)]?.focus();
  }, [value.length]);
  const setAt = (i, ch) => {
    const arr = digits.slice();
    arr[i] = ch;
    onChange(arr.join("").replace(/\D/g, "").slice(0, 6));
  };
  return (
    <div className="flex items-start gap-[10px] max-sm:w-full max-sm:gap-2" onPaste={(e) => {
      const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
      if (text) { e.preventDefault(); onChange(text); }
    }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={d}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => setAt(i, e.target.value.replace(/\D/g, "").slice(-1))}
          onKeyDown={(e) => { if (e.key === "Backspace" && !d && i > 0) { setAt(i - 1, ""); refs.current[i - 1]?.focus(); } }}
          className={`h-[62px] w-[50px] rounded-[12px] border bg-white text-center bm-h-l text-bm-ink outline-none max-sm:w-auto max-sm:min-w-0 max-sm:max-w-[50px] max-sm:flex-1 max-sm:px-0 ${
            error ? "border-bm-critical shadow-[inset_0_0_0_1px_#e2241a]" : "border-bm-border focus:border-bm-ink focus:shadow-[inset_0_0_0_1px_#2c2c2c]"
          }`}
        />
      ))}
    </div>
  );
};
